import { useSyncExternalStore } from "react";
import { flushSync } from "react-dom";
import { prefersReducedMotion } from "./motion";

// Light (white) is the default; only an explicit "dark" choice switches it.
const STORAGE_KEY = "portfolio-theme";
const THEME_COLORS = { dark: "#0a0c0b", light: "#ffffff" };
const listeners = new Set();

const readSaved = () => {
    try {
        return localStorage.getItem(STORAGE_KEY) === "dark";
    } catch (e) {
        return false;
    }
};

let dark = readSaved();

const apply = () => {
    document.documentElement.classList.toggle("dark", dark);
    const meta = document.querySelector('meta[name="theme-color"]');
    if (meta) meta.setAttribute("content", dark ? THEME_COLORS.dark : THEME_COLORS.light);
};

const setDark = (next) => {
    dark = next;
    try {
        localStorage.setItem(STORAGE_KEY, next ? "dark" : "light");
    } catch (e) {
        // Storage can be unavailable (private mode); the theme still applies for this visit.
    }
    apply();
    listeners.forEach((listener) => listener());
};

if (typeof document !== "undefined") apply();

const subscribe = (listener) => {
    listeners.add(listener);
    return () => listeners.delete(listener);
};

export const useDarkMode = () => useSyncExternalStore(subscribe, () => dark);

// Switches theme with a circular wipe from `origin` (an element) where View Transitions exist.
export function toggleTheme(origin) {
    const next = !dark;
    const commit = () => flushSync(() => setDark(next));

    if (typeof document.startViewTransition !== "function" || prefersReducedMotion()) {
        commit();
        return;
    }

    const rect = origin && origin.getBoundingClientRect ? origin.getBoundingClientRect() : null;
    const x = rect ? rect.left + rect.width / 2 : window.innerWidth / 2;
    const y = rect ? rect.top + rect.height / 2 : window.innerHeight / 2;
    const radius = Math.hypot(Math.max(x, window.innerWidth - x), Math.max(y, window.innerHeight - y));

    document
        .startViewTransition(commit)
        .ready.then(() => {
            document.documentElement.animate(
                { clipPath: [`circle(0px at ${x}px ${y}px)`, `circle(${radius}px at ${x}px ${y}px)`] },
                { duration: 700, easing: "cubic-bezier(0.76, 0, 0.24, 1)", pseudoElement: "::view-transition-new(root)" }
            );
        })
        .catch(() => {});
}
